/**
 * Supabase client singleton for the Lineage app.
 *
 * Tethered to the GeneX Supabase project (shared with Ledger/Health/etc for
 * now — see sacred/the-surfaces.md "Auth architecture" note on why we're
 * staying single-project in 2026). This client uses the anon key and is
 * subject to RLS policies on core.Contact + any Orchist-specific tables.
 *
 * Auth: Supabase Auth with email OTP via custom SMTP (Resend on orchist.org,
 * sender `theline@orchist.org`).
 *
 * Usage:
 *   import { supabase } from '@/lib/supabase';
 *   const { data, error } = await supabase.auth.signInWithOtp({ email });
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Copy .env.example to .env.local (dev) or set in Vercel project env (prod).'
  );
}

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Persist session in localStorage (standard SPA pattern)
    persistSession: true,
    // Refresh the access token automatically before expiry
    autoRefreshToken: true,
    // Detect session in URL for magic-link callbacks
    detectSessionInUrl: true,
    // Flow type: 'implicit' (NOT pkce) for cross-device magic links.
    //
    // PKCE is more secure but device-locked: the magic link click must
    // happen in the same browser context that initiated the OTP request,
    // because the click → exchangeCodeForSession step needs the
    // code_verifier from localStorage. That breaks the invitation flow,
    // where the inviter sends from one browser and the invitee receives
    // and clicks from a completely different device/profile.
    //
    // Implicit puts the access_token in the URL fragment, readable from
    // any browser context. Trade-off: slightly higher referrer-leak
    // surface; acceptable for a Lineage app whose invitees come from
    // anywhere. Phase G real-case testing 2026-04-25 forced this fix.
    flowType: 'implicit',
  },
});

export type { SupabaseClient, Session, User } from '@supabase/supabase-js';
